import React, { useEffect, useState, useCallback } from 'react';
import api, { BACKEND_URL } from '../utils/api';
import { isDevAuthEnabled } from '../utils/devAuth';

const Dot = ({ ok, pulse }) => (
  <span
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: 999,
      background: ok ? '#22c55e' : '#ef4444',
      marginRight: 8,
      animation: pulse ? 'pulse 1.5s infinite' : 'none',
      boxShadow: ok && pulse ? '0 0 8px #22c55e' : 'none'
    }}
    aria-hidden="true"
  />
);

const Row = ({ label, ok, extra, pulse }) => (
  <div className="flex items-center text-sm text-slate-300">
    <Dot ok={ok} pulse={pulse} />
    <span className="mr-2">{label}</span>
    {extra ? <span className="text-slate-500">({extra})</span> : null}
  </div>
);

const SystemStatus = () => {
  const [pngLogo, setPngLogo] = useState(false);
  const [backendOk, setBackendOk] = useState(false);
  const [authOk, setAuthOk] = useState(false);
  const [projectsOk, setProjectsOk] = useState(false);
  const [chatOk, setChatOk] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [notes, setNotes] = useState([]);

  // Agent status state
  const [agentStatus, setAgentStatus] = useState(null);
  const [agentOnline, setAgentOnline] = useState(false);
  const [heartbeat, setHeartbeat] = useState(0);

  // Fetch agent status
  const fetchAgentStatus = useCallback(() => {
    fetch(`${BACKEND_URL}/api/agent/status`, { credentials: 'include' })
      .then((r) => {
        if (r.ok) {
          setAgentOnline(true);
          setHeartbeat(h => h + 1);
          return r.json();
        }
        setAgentOnline(false);
        return null;
      })
      .then((json) => {
        if (json) {
          setAgentStatus(json);
        }
      })
      .catch(() => {
        setAgentOnline(false);
        setAgentStatus(null);
      });
  }, []);

  useEffect(() => {
    // PNG logo detection
    const img = new Image();
    img.onload = () => setPngLogo(true);
    img.onerror = () => setPngLogo(false);
    img.src = '/digital-ninja-logo.png';

    // Dev mode
    setDevMode(isDevAuthEnabled());

    // Auth token present
    const token = localStorage.getItem('token');
    setAuthOk(Boolean(token));

    // Backend health
    fetch(`${BACKEND_URL}/api/health`, { credentials: 'include' })
      .then((r) => {
        if (r.ok) {
          setBackendOk(true);
          return r.json();
        }
        setBackendOk(false);
        return null;
      })
      .then((json) => {
        if (json?.status !== 'ok') {
          setNotes((n) => [...n, 'Health check returned non-ok status']);
        }
      })
      .catch(() => setBackendOk(false));

    // Projects API
    api.get('/projects')
      .then(() => setProjectsOk(true))
      .catch(() => setProjectsOk(false));

    // Chat endpoint basic reachability
    fetch(`${BACKEND_URL}/api/chat/health`, {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    })
      .then((r) => setChatOk(r.ok))
      .catch(() => setChatOk(false));

    // Agent status - initial fetch
    fetchAgentStatus();

    // Heartbeat polling every 5 seconds
    const heartbeatInterval = setInterval(fetchAgentStatus, 5000);

    return () => clearInterval(heartbeatInterval);
  }, [fetchAgentStatus]);

  return (
    <div className="mt-2 rounded-md border border-slate-700 bg-slate-800/70 p-3">
      {/* Agent Self-Awareness Section */}
      <div className="mb-4 p-3 rounded-md border border-cyan-900/50 bg-gradient-to-r from-cyan-950/30 to-slate-800/50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <Dot ok={agentOnline} pulse={agentOnline} />
            <span className="text-cyan-400 font-medium">Digital Ninja Agent</span>
          </div>
          <span className="text-xs text-slate-500">
            Heartbeat #{heartbeat}
          </span>
        </div>

        {agentStatus ? (
          <div className="space-y-2">
            {/* Current State */}
            <div className="flex items-center text-sm">
              <span className="text-slate-400 mr-2">State:</span>
              <span className={`font-medium ${agentStatus.state === 'idle' ? 'text-green-400' :
                agentStatus.state === 'thinking' ? 'text-yellow-400' :
                  agentStatus.state === 'executing' ? 'text-blue-400' :
                    agentStatus.state === 'error' ? 'text-red-400' : 'text-slate-300'
                }`}>
                {agentStatus.state?.toUpperCase() || 'UNKNOWN'}
              </span>
            </div>

            {/* Last Action */}
            {agentStatus.last_action && (
              <div className="text-xs text-slate-400">
                Last action: <span className="text-slate-300">{agentStatus.last_action}</span>
              </div>
            )}

            {/* Error Message */}
            {agentStatus.error_message && (
              <div className="text-xs text-red-400 bg-red-950/30 p-2 rounded">
                ⚠️ {agentStatus.error_message}
              </div>
            )}

            {/* Capabilities */}
            <div className="mt-2">
              <div className="text-xs text-slate-500 mb-1">I can:</div>
              <div className="flex flex-wrap gap-1">
                {agentStatus.capabilities?.slice(0, 4).map((cap, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-green-900/30 text-green-400 rounded">
                    {cap}
                  </span>
                ))}
              </div>
            </div>

            {/* Limitations */}
            <div className="mt-1">
              <div className="text-xs text-slate-500 mb-1">I cannot:</div>
              <div className="flex flex-wrap gap-1">
                {agentStatus.limitations?.slice(0, 2).map((lim, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-red-900/30 text-red-400 rounded">
                    {lim}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-slate-500">
            {agentOnline ? 'Loading agent status...' : 'Agent offline - backend may not be running'}
          </div>
        )}
      </div>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">System Status</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-6">
        <Row
          label="Logo PNG detected"
          ok={pngLogo}
          extra={pngLogo ? 'PNG in use' : 'Fallback SVG in use'}
        />
        <Row
          label="Backend health"
          ok={backendOk}
          extra={BACKEND_URL}
        />
        <Row
          label="Auth token present"
          ok={authOk}
        />
        <Row
          label="Projects API reachable"
          ok={projectsOk}
        />
        <Row
          label="Chat endpoint reachable"
          ok={chatOk}
          extra={chatOk ? 'OK' : devMode ? 'Dev fallback available' : 'Unreachable'}
        />
        <Row
          label="Agent online"
          ok={agentOnline}
          pulse={agentOnline}
          extra={agentStatus?.state || 'checking...'}
        />
        <Row
          label="Dev mode (fallbacks enabled)"
          ok={devMode}
        />
      </div>
      {notes.length > 0 ? (
        <div className="mt-2 text-xs text-amber-400">
          {notes.map((n, i) => (
            <div key={i}>• {n}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default SystemStatus;